import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import { CargoPaymentInfo } from 'src/app/models/cargo-payment-info.model';
import { Device } from 'src/app/models/device.model';
import { CustomMessageType } from 'src/app/models/enums/custom-message-type.enum';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { CookieService } from 'src/app/services/cookieService/cookie.service';
import { ModalService } from 'src/app/services/modal-service/modal.service';
import { PaymentService } from 'src/app/services/paymentService/payment.service';
import { SharedService } from 'src/app/services/shared-service/shared.service';
import { SignalRService } from 'src/app/services/signal-r-service/signal-r.service';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';
import { MonitoringService } from 'src/app/services/monitoring-service/monitoring.service';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalComponent implements OnInit {
  @Input() cargoPaymentInfo: CargoPaymentInfo | undefined;
  @Input() modalType = '';
  @Input() transactionId: string | undefined;
  ipadButton!: HTMLElement | null;
  display$: Observable<'open' | 'close'> | undefined;
  @Output() clickContinueEvent = new EventEmitter<boolean>();
  currentErrorCreditCard = false;
  currentNextClick = false;
  deviceList: Device[];
  filteredDeviceList: Device[];
  selectedDevice: Device | null;
  searchBar!: HTMLElement | null;
  isJasmineTest = false;
  isManualPaymentContinueDisabled = true;
  isBluefinTokenRetry = false;
  airportCode = '';
  private currentCorrelationId = '';

  constructor(
    private modalService: ModalService,
    private signalRService: SignalRService,
    public sharedService: SharedService,
    private cookieService: CookieService,
    public paymentService: PaymentService,
    private route: ActivatedRoute,
    private monitoringService: MonitoringService
  ) {
    this.deviceList = [];
    this.filteredDeviceList = [];
    this.selectedDevice = null;
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params: { [key: string]: string }) => {
      if (params['airportCode']) {
        this.airportCode = params['airportCode'].toUpperCase();
      }
      this.filterDevicesAirportCode();
    });
    this.sharedService.currentTransactionId.subscribe((transactionId) => (this.currentCorrelationId = transactionId));
    this.sharedService.currentNextClick.subscribe((isNextClick) => (this.currentNextClick = isNextClick));
    this.sharedService.currentErrorCreditCard.subscribe((isErrorCreditCard) => (this.currentErrorCreditCard = isErrorCreditCard));
    this.display$ = this.modalService.watch();
    this.sharedService.currentDeviceList.subscribe((deviceList) => {
      this.deviceList = deviceList;
      this.filterDevicesAirportCode();
    });
    this.sharedService.currentIsBluefinTokenRetry.subscribe((isBluefinTokenRetry) => (this.isBluefinTokenRetry = isBluefinTokenRetry));
  }

  filterDevicesAirportCode() {
    if (!this.airportCode || this.deviceList.length === 0) {
      this.filteredDeviceList = [...this.deviceList];
      return;
    }
    this.filteredDeviceList = this.deviceList.filter((device) => device.deviceId.startsWith(this.airportCode));
  }

  checkSearchTermLength(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const term = document.getElementById('searchBar') as HTMLInputElement;

    return (
      term != null &&
      term.value != null &&
      term.value.length > 0 &&
      term.value != "selectedDevice !== null ? selectedDevice.deviceId : ''" &&
      term.value != this.selectedDevice?.deviceId
    );
  }

  close(): void {
    if (this.modalType === 'connectIpad' || this.modalType === 'deviceList' || this.modalType === 'noDevices') {
      this.selectedDevice = null;
    }
    this.isManualPaymentContinueDisabled = true;
    this.modalService.close();
  }

  onConfirmExit(): void {
    if (this.modalType === 'exitToiCargo') {
      this.exitToiCargo();
    }
    this.modalService.close();
  }

  onContinue(): void {
    this.sharedService.updateClickContinueEvent(true);
  }

  onManualContinue(): void {
    this.sharedService.updateClickManualContinueEvent(true);
  }

  onContinueDeviceSelected(): void {
    if (this.isContinueDeviceSelected()) {
      return;
    }

    if (this.modalType === 'deviceList' && this.selectedDevice !== null) {
      this.sharedService.updateConnectedIpad(false);
      this.sharedService.updateContinueDeviceClick(true);
      this.sharedService.updateSelectedIpad(this.selectedDevice);

      mapMessage.set(CustomMessage.SuccessDevice, {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        text: 'Device ' + this.selectedDevice.deviceId + ' successfully selected.',
        type: CustomMessageType.Success,
      });

      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessDevice));
      this.sharedService.updateClickContinueEvent(true);
      this.monitoringService.logEvent(
        'CargoPaymentUI.Pairing.Started',
        this.createTelemetryProperties({
          ipadDeviceId: this.selectedDevice.deviceId,
        })
      );
      this.signalRService.pairWithConnectedDevice(this.selectedDevice.deviceId);
      this.setPairedDeviceToCookie(this.selectedDevice);
      this.modalService.close();
    }
  }

  isContinueDeviceSelected(): boolean {
    const term = document.getElementById('searchBar') as HTMLInputElement;
    return this.modalType === 'deviceList' && (this.selectedDevice === null || term === null || term.value === null);
  }

  ipadSelected(selectedDevice: Device): void {
    this.selectedDevice = selectedDevice;
  }

  setPairedDeviceToCookie(pairedDevice: Device): void {
    this.cookieService.setCookie('pairedDevice', pairedDevice.deviceId, 100000);
  }

  public exitToiCargo() {
    const paymentAdvice = this.cargoPaymentInfo?.paymentAdviceNumber || '';
    const awb = this.cargoPaymentInfo?.awbNumber || '';
    //This validation is mandatory because redirection breaks jasmin test, in test set this value to true
    if (this.isJasmineTest === false) {
      const redirectTo = environment.iCargoBaseUrlRedirect + '?screenMode=closeScreenAction&paymentAdvice=' + paymentAdvice + '&awb=' + awb;
      console.log('Redirecting to iCargo URL: ' + redirectTo);
      window.location.href = redirectTo;
    }
  }

  private createTelemetryProperties(properties: { [key: string]: unknown } = {}): { [key: string]: unknown } {
    const telemetryProperties = {
      correlationId: this.transactionId || this.currentCorrelationId || this.cargoPaymentInfo?.transactionId || undefined,
      paymentAdviceNumber: this.cargoPaymentInfo?.paymentAdviceNumber,
      awb: this.cargoPaymentInfo?.awbNumber,
      browserDeviceId: this.signalRService.connectedDevice?.deviceId,
      ...properties,
    };

    return Object.fromEntries(Object.entries(telemetryProperties).filter(([, value]) => value !== undefined && value !== null));
  }
}
